/**
 * The Lanes tab's view model (docs/ux/project-tab-spec.md §1.4–1.7): a pure join of the project's
 * workstreams, tasks, runs (unified run state + live activity), inbox asks, landings and builds.
 * Nothing here is stored; every render recomputes it, so it cannot go stale.
 */
import {
  AGENT_NAME,
  LANE_COLUMNS,
  type LaneColumn,
  type LaneState,
  laneTaskState,
  localDayStart,
  RUN_PHASE_LABEL,
  type RunPhase,
} from '@viko/transcript-ui'
import { describeAsk } from './ask-view.js'
import { isSnoozed, type Snoozes } from './inbox.js'
import { autoLandsOnFinish } from './landing-status.js'
import { type ReleaseRow, releaseStrip } from './release-lanes.js'
import type {
  BuildCard,
  InboxItem,
  Landing,
  Machine,
  Project,
  RunSummary,
  TaskDetail,
  WorkstreamDetail,
} from './types.js'
import { selectedAccount } from './usage-accounting.js'

/** Live run state reported by the lane header's transcript subscription (overrides the summary). */
export interface LivePhase {
  phase: RunPhase
  label: string
  reason?: string
  /** Live activity "now" line. */
  line?: string
  /** A step or reply is streaming right now. */
  streaming?: boolean
  /** Short verified-evidence text ("38 tests passing so far"). */
  evidence?: string
  /** auto-artifacts: artifacts the run posted (screenshots, pages, Claude artifacts). */
  artifacts?: number
}

export interface LanesInput {
  project: Pick<Project, 'id' | 'name' | 'loop' | 'config' | 'machines'>
  workstreams: readonly WorkstreamDetail[]
  tasks: readonly TaskDetail[]
  runs: readonly RunSummary[]
  inbox: readonly InboxItem[]
  landings: readonly Landing[]
  builds: readonly BuildCard[]
  machines: readonly Machine[]
  live?: ReadonlyMap<string, LivePhase>
  /** Hub connection; anything but live freezes live lines (degraded). */
  connection?: string
  locked?: boolean
  snoozes?: Snoozes
  /**
   * The app-wide needs-you count for a project (lib/needs-you.ts). When given, the tab's
   * counts use it so the tab, its badge, Overview and the Inbox always show the same number.
   */
  needsYou?: (projectId: string) => number
  now: number
}

export interface LaneAgent {
  runId: string
  agent: 'claude' | 'codex'
  /** "Claude on Mac" (+ account when the machine has more than one). */
  label: string
  title: string
  phase: RunPhase
  phaseLabel: string
  line: string
  streaming: boolean
  waiting: boolean
  taskId?: string
}

export interface LaneChip {
  kind: 'evidence' | 'commit' | 'land' | 'provenance' | 'agent' | 'artifacts'
  label: string
  tone?: 'ok' | 'run' | 'wait' | 'fail' | 'gate'
}

export interface LaneCard {
  id: string
  title: string
  state: LaneState
  column: LaneColumn | null
  subLabel?: string
  reason?: string
  runId?: string
  agent?: LaneAgent
  chips: LaneChip[]
  askId?: string
  askTitle?: string
  proposed?: boolean
  landedAt?: number
  workstreamId: string
  laneName: string
}

export interface Lane {
  id: string
  name: string
  /** Not a real workstream (tasks without one, or sessions started outside a workstream). */
  pseudo?: boolean
  paused: boolean
  collapsed: boolean
  agents: LaneAgent[]
  /** Why no agent is on this lane right now. */
  idleReason?: string
  columns: Record<LaneColumn, LaneCard[]>
  /** Landed before today ("See what landed"). */
  earlier: LaneCard[]
  hot: boolean
}

export interface AskOption {
  label: string
  description?: string
  suggested: boolean
}
export interface AskRow {
  id: string
  runId: string
  kind: InboxItem['type']
  title: string
  question: string
  options: AskOption[]
  blocking: boolean
  score: number
  leverage: string[]
  createdAt: number
  locked: boolean
  taskId?: string
  workstreamId?: string
  workstreamName?: string
  agentLabel?: string
  seq: number
  item: InboxItem
}

/** One row of the landing and release strip (lib/release-lanes.ts). */
export type BuildRow = ReleaseRow

export interface CapacityInfo {
  machines: string[]
  text: string
  picks: LaneCard[]
}

export type LanesState = 'empty' | 'busy' | 'needs-you' | 'green' | 'idle'

export interface LanesModel {
  projectId: string
  name: string
  state: LanesState
  lanes: Lane[]
  asks: AskRow[]
  /** At least one ask blocks a run (hot asks panel). */
  hotAsks: boolean
  /** Landing to main, then one row per configured release lane. */
  builds: BuildRow[]
  capacity?: CapacityInfo
  counts: { needs: number; running: number; landedToday: number; queued: number }
  degraded: { hub: boolean; machines: string[] }
  locked: boolean
  autoLand: boolean
  cards: LaneCard[]
}

const LIVE_STATUS = new Set(['running', 'waiting', 'compacting'])
export const isLiveRun = (r: RunSummary) => LIVE_STATUS.has(String(r.status)) || !!r.typingElsewhere

/** Run phase from the hub's run summary, before (or without) a transcript subscription. */
export function summaryPhase(r: RunSummary, machineOnline = true): RunPhase {
  if (isLiveRun(r) && !machineOnline) return 'offline'
  if (r.status === 'waiting') return 'waiting'
  if (r.status === 'running' || r.status === 'compacting' || r.typingElsewhere) return 'working'
  if (r.status === 'error') return 'failed'
  if (r.landing?.state === 'landed') return 'landed'
  if (r.landing?.state === 'ready') return 'ready'
  if (r.status === 'idle') return 'idle'
  return 'finished'
}

/** Which project an inbox item belongs to (its run's project, else the hub's project name). */
export function itemProject(
  item: InboxItem,
  runs: ReadonlyMap<string, RunSummary>,
  projects: readonly Pick<Project, 'id' | 'name'>[],
): string | undefined {
  const run = runs.get(item.runId)
  if (run?.projectId) return run.projectId
  if (item.projectName) return projects.find((p) => p.name === item.projectName)?.id
  return undefined
}

/** Per-project tab badges: the same filters as the Inbox badge and the Now count. */
export function tabCounts(
  projects: readonly Pick<Project, 'id' | 'name'>[],
  runs: readonly RunSummary[],
  inbox: readonly InboxItem[],
  now: number,
  snoozes: Snoozes = {},
): Map<string, { needs: number; running: number }> {
  const out = new Map(projects.map((p) => [p.id, { needs: 0, running: 0 }]))
  const byRun = new Map(runs.map((r) => [r.runId, r]))
  for (const r of runs) {
    const c = r.projectId ? out.get(r.projectId) : undefined
    if (c && (r.status === 'running' || r.status === 'waiting' || r.status === 'compacting'))
      c.running++
  }
  for (const i of inbox) {
    if (!i.blocking || isSnoozed(snoozes, i.id, now)) continue
    const id = itemProject(i, byRun, projects)
    const c = id ? out.get(id) : undefined
    if (c) c.needs++
  }
  return out
}

export function waitedText(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60_000))
  if (m < 1) return 'just now'
  if (m < 60) return `waiting ${m} min`
  const h = Math.round(m / 60)
  if (h < 48) return `waiting ${h} h`
  return `waiting ${Math.round(h / 24)} d`
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

function agentLabel(r: RunSummary, machines: readonly Machine[]): string {
  const m = machines.find((x) => x.id === r.machineId)
  const who = `${AGENT_NAME[r.agent ?? 'claude']}${m ? ` on ${m.name}` : ''}`
  const accounts = (m?.providerAccounts ?? []).filter(
    (a) => a.enabled && a.provider === (r.agent ?? 'claude'),
  )
  if (accounts.length < 2) return who
  const acct = selectedAccount(r, machines)
  return acct ? `${who} · ${acct.label}` : who
}

function toAgent(
  r: RunSummary,
  machines: readonly Machine[],
  live: LivePhase | undefined,
  frozen: boolean,
): LaneAgent {
  const online = machines.find((m) => m.id === r.machineId)?.online ?? true
  // An offline machine outranks the last live report (its transcript cannot move any more).
  const phase = !online ? 'offline' : (live?.phase ?? summaryPhase(r, online))
  const phaseLabel = !online ? RUN_PHASE_LABEL.offline : (live?.label ?? RUN_PHASE_LABEL[phase])
  const line =
    (online ? live?.line : undefined) ??
    (r.status === 'waiting' && r.blockedOn
      ? `Waiting for you: ${r.blockedOn}`
      : phase === 'offline'
        ? `${machines.find((m) => m.id === r.machineId)?.name ?? 'Machine'} is offline`
        : (r.activity ?? phaseLabel))
  return {
    runId: r.runId,
    agent: r.agent ?? 'claude',
    label: agentLabel(r, machines),
    title: r.title ?? r.taskTitle ?? r.runId,
    phase,
    phaseLabel,
    line,
    streaming: !frozen && (live?.streaming ?? (phase === 'working' || phase === 'making-progress')),
    waiting: phase === 'waiting',
    ...(r.taskId ? { taskId: r.taskId } : {}),
  }
}

const optionSuggested = (label: string) => /\(recommended\)|\bsuggested\b/i.test(label)

function toAsk(
  i: InboxItem,
  run: RunSummary | undefined,
  machines: readonly Machine[],
  now: number,
): AskRow {
  let title = i.title
  let question = i.title
  let options: AskOption[] = []
  if (i.event) {
    const d = describeAsk(i.event)
    title = d.title === 'Question' ? i.title || d.body : d.title
    question = d.body || i.title
    if (i.event.kind === 'question')
      options = (i.event.questions[0]?.options ?? []).map((o) => ({
        label: o.label.replace(/\s*\((recommended|suggested)\)\s*/i, '').trim(),
        ...(o.description ? { description: o.description } : {}),
        suggested: optionSuggested(o.label),
      }))
    else
      options = [
        { label: 'Allow', suggested: false },
        { label: 'Deny', suggested: false },
      ]
  } else if (i.type === 'land' && i.land) {
    title = `Land ${i.land.branch}?`
    question = `${plural(i.land.commits, 'commit')} · ${plural(i.land.files, 'file')} · +${i.land.additions} −${i.land.deletions}`
  }
  const leverage: string[] = []
  if (i.unblocks) leverage.push(`Unblocks ${plural(i.unblocks, 'task')}`)
  else if (i.blocking) leverage.push('Blocks a run')
  else leverage.push('Blocks nothing')
  leverage.push(waitedText(now - i.createdAt))
  const kind = i.type
  const wsId = i.workstreamId ?? run?.workstreamId
  const wsName = i.workstreamName ?? run?.workstreamName
  return {
    id: i.id,
    runId: i.runId,
    kind,
    title: i.locked ? i.title : title,
    question: i.locked ? '' : question,
    options: i.locked ? [] : options,
    blocking: i.blocking,
    score: i.score,
    leverage,
    createdAt: i.createdAt,
    locked: !!i.locked,
    seq: i.seq,
    item: i,
    ...(run?.taskId ? { taskId: run.taskId } : {}),
    ...(wsId ? { workstreamId: wsId } : {}),
    ...(wsName ? { workstreamName: wsName } : {}),
    ...(run ? { agentLabel: agentLabel(run, machines) } : {}),
  }
}

function evidenceChip(live: LivePhase | undefined, state: LaneState): LaneChip | undefined {
  if (!live?.evidence) return undefined
  return {
    kind: 'evidence',
    label: live.evidence,
    tone: state === 'landed' || state === 'verifying' ? 'ok' : 'run',
  }
}

function provenanceChip(t: TaskDetail): LaneChip | undefined {
  if (t.barkIds?.length) return { kind: 'provenance', label: 'From your request' }
  if (t.provenance === 'agent') return { kind: 'provenance', label: 'Suggested by an agent' }
  if (t.provenance === 'brain') return { kind: 'provenance', label: 'From a capture' }
  return undefined
}

const CARD_WEIGHT: Partial<Record<LaneState, number>> = { 'needs-you': 0, blocked: 1 }
function sortCards(cards: LaneCard[], tasks: ReadonlyMap<string, TaskDetail>): LaneCard[] {
  return cards.sort((a, b) => {
    const ta = tasks.get(a.id),
      tb = tasks.get(b.id)
    return (
      (CARD_WEIGHT[a.state] ?? 2) - (CARD_WEIGHT[b.state] ?? 2) ||
      (ta?.rank ?? Number.POSITIVE_INFINITY) - (tb?.rank ?? Number.POSITIVE_INFINITY) ||
      (tb?.priority ?? 0) - (ta?.priority ?? 0) ||
      (b.landedAt ?? 0) - (a.landedAt ?? 0) ||
      (ta?.createdAt ?? 0) - (tb?.createdAt ?? 0)
    )
  })
}

const emptyColumns = (): Record<LaneColumn, LaneCard[]> => ({
  queued: [],
  running: [],
  verifying: [],
  landed: [],
})

export function buildLanesModel(input: LanesInput): LanesModel {
  const { project, now, machines } = input
  const pid = project.id
  const frozen = !!input.connection && input.connection !== 'live' && input.connection !== 'idle'
  const autoLand = autoLandsOnFinish(project)
  const runs = input.runs.filter((r) => r.projectId === pid)
  const runById = new Map(input.runs.map((r) => [r.runId, r]))
  const workstreams = input.workstreams
    .filter((w) => w.projectId === pid && w.status !== 'archived')
    .sort(
      (a, b) =>
        Number(a.paused) - Number(b.paused) ||
        (a.rank ?? Number.POSITIVE_INFINITY) - (b.rank ?? Number.POSITIVE_INFINITY) ||
        a.createdAt - b.createdAt,
    )
  const tasks = input.tasks.filter((t) => t.projectId === pid)
  const taskById = new Map(tasks.map((t) => [t.id, t]))
  const agents = new Map(
    runs
      .filter(isLiveRun)
      .map((r) => [r.runId, toAgent(r, machines, input.live?.get(r.runId), frozen)]),
  )

  // Asks: this project's Inbox items (questions, approvals, landings, cards), by leverage — the
  // same items the tab badge counts.
  const asks = input.inbox
    .filter(
      (i) =>
        !isSnoozed(input.snoozes ?? {}, i.id, now) && itemProject(i, runById, [project]) === pid,
    )
    .map((i) => toAsk(i, runById.get(i.runId), machines, now))
    .sort((a, b) => Number(b.blocking) - Number(a.blocking) || b.score - a.score)
  const askByTask = new Map<string, AskRow>()
  // Only asks that block their run turn a card into "needs you"; the rest wait in the asks panel.
  for (const a of asks) {
    if (!a.blocking) continue
    if (a.taskId && !askByTask.has(a.taskId)) askByTask.set(a.taskId, a)
    for (const t of tasks) if (t.runId === a.runId && !askByTask.has(t.id)) askByTask.set(t.id, a)
  }

  const landingFor = (t: TaskDetail) =>
    input.landings.find(
      (l) =>
        (l.state === 'queued' || l.state === 'running') &&
        (l.taskIds.includes(t.id) || (!!t.runId && l.runId === t.runId)),
    )
  const landedFor = (t: TaskDetail) =>
    input.landings.find((l) => l.state === 'landed' && l.taskIds.includes(t.id))

  const cardOf = (t: TaskDetail, laneName: string): LaneCard => {
    const runId = t.runId ?? runs.find((r) => r.taskId === t.id && isLiveRun(r))?.runId ?? undefined
    const run = runId ? runById.get(runId) : undefined
    const agent = runId ? agents.get(runId) : undefined
    const live = runId ? input.live?.get(runId) : undefined
    const online = run ? (machines.find((m) => m.id === run.machineId)?.online ?? true) : true
    const phase = run
      ? online
        ? (live?.phase ?? summaryPhase(run, online))
        : 'offline'
      : undefined
    const landing = landingFor(t)
    const ask = askByTask.get(t.id)
    const v = laneTaskState(
      { status: t.status, landedAt: t.landedAt, blockedReason: t.blockedReason },
      phase ? { phase, ...(live?.reason ? { reason: live.reason } : {}) } : undefined,
      landing ? { active: true, step: landing.step } : undefined,
      { now, openDecision: !!ask },
    )
    const chips: LaneChip[] = []
    const ev = evidenceChip(live, v.state)
    if (ev) chips.push(ev)
    const landed = v.state === 'landed' ? landedFor(t) : undefined
    if (landed?.commit)
      chips.push({ kind: 'commit', label: `${landed.commit.slice(0, 7)} verified`, tone: 'ok' })
    if (v.state !== 'landed')
      chips.push(
        autoLand
          ? { kind: 'land', label: 'Lands when checks pass' }
          : { kind: 'land', label: 'You review before landing', tone: 'gate' },
      )
    const prov = provenanceChip(t)
    if (prov) chips.push(prov)
    // ---- auto-artifacts ----
    if (live?.artifacts)
      chips.push({
        kind: 'artifacts',
        label: `${live.artifacts.toLocaleString('en-US')} artifact${live.artifacts === 1 ? '' : 's'}`,
      })
    // ---- end auto-artifacts ----
    return {
      id: t.id,
      title: t.title,
      state: v.state,
      column: v.column,
      chips,
      workstreamId: t.workstreamId ?? '',
      laneName,
      ...(v.subLabel ? { subLabel: v.subLabel } : {}),
      ...(v.reason ? { reason: v.reason } : {}),
      ...(v.proposed ? { proposed: true } : {}),
      ...(runId ? { runId } : {}),
      ...(agent ? { agent } : {}),
      ...(ask ? { askId: ask.id, askTitle: ask.title } : {}),
      ...(t.landedAt ? { landedAt: t.landedAt } : {}),
    }
  }

  const machineName = (id: string | null | undefined) =>
    id ? machines.find((m) => m.id === id) : undefined
  const lanes: Lane[] = []
  const makeLane = (
    id: string,
    name: string,
    laneTasks: TaskDetail[],
    laneRuns: RunSummary[],
    w?: WorkstreamDetail,
  ): Lane => {
    const columns = emptyColumns()
    const earlier: LaneCard[] = []
    for (const t of laneTasks) {
      const c = cardOf(t, name)
      if (c.column) columns[c.column].push(c)
      else if (c.state === 'landed') earlier.push(c)
    }
    for (const k of Object.keys(columns) as LaneColumn[]) sortCards(columns[k], taskById)
    const laneAgents = laneRuns.filter(isLiveRun).map((r) => agents.get(r.runId) as LaneAgent)
    const open = columns.queued.length + columns.running.length + columns.verifying.length
    const lastTouch = Math.max(
      w?.updatedAt ?? 0,
      ...laneTasks.map((t) => t.updatedAt),
      ...laneRuns.map((r) => r.updatedAt ?? 0),
    )
    const paused = !!w?.paused
    let idleReason: string | undefined
    if (!laneAgents.length) {
      const m = machineName(w?.machineId ?? null)
      idleReason = paused
        ? 'Paused by you'
        : m && !m.online
          ? `${m.name} is offline`
          : columns.queued.some((c) => c.state === 'queued' && !c.proposed)
            ? 'Next queued task starts when capacity frees up.'
            : open
              ? 'Nothing is running in this lane.'
              : 'Nothing queued.'
    }
    const hot = [...columns.queued, ...columns.running, ...columns.verifying].some(
      (c) => c.state === 'needs-you' || c.state === 'blocked',
    )
    return {
      id,
      name,
      paused,
      collapsed: !open && !laneAgents.length && now - lastTouch > 24 * 3_600_000,
      agents: laneAgents,
      columns,
      earlier,
      hot,
      ...(idleReason ? { idleReason } : {}),
      ...(w ? {} : { pseudo: true }),
    }
  }
  for (const w of workstreams)
    lanes.push(
      makeLane(
        w.id,
        w.name,
        tasks.filter((t) => t.workstreamId === w.id),
        runs.filter((r) => r.workstreamId === w.id),
        w,
      ),
    )
  const known = new Set(workstreams.map((w) => w.id))
  const loose = tasks.filter((t) => !t.workstreamId || !known.has(t.workstreamId))
  const looseRuns = runs.filter((r) => !r.workstreamId || !known.has(r.workstreamId))
  if (loose.length || looseRuns.some(isLiveRun))
    lanes.push(makeLane('_loose', 'Not in a workstream', loose, looseRuns))

  const cards = lanes.flatMap((l) => Object.values(l.columns).flat())
  const count = (s: LaneState) => cards.filter((c) => c.state === s).length
  const landedToday = cards.filter((c) => c.column === 'landed').length
  const running = runs.filter(
    (r) => r.status === 'running' || r.status === 'waiting' || r.status === 'compacting',
  ).length
  const needs = input.needsYou ? input.needsYou(pid) : asks.filter((a) => a.blocking).length
  // Verifying cards wait on the landing pipeline, not an agent: they do not keep a tab "busy".
  const inMotion = running + count('running') + count('landing')
  const hotAsks = needs > 0

  const empty = !workstreams.length && !tasks.length && !runs.length
  const state: LanesState = empty
    ? 'empty'
    : hotAsks || count('needs-you') > 0
      ? 'needs-you'
      : inMotion > 0
        ? 'busy'
        : landedToday > 0 && !asks.length
          ? 'green'
          : 'idle'

  const offlineMachines = project.machines
    .map((id) => machines.find((m) => m.id === id))
    .filter((m): m is Machine => !!m && !m.online)
    .map((m) => m.name)

  let capacity: CapacityInfo | undefined
  const queued = cards.filter((c) => c.state === 'queued' && !c.proposed)
  if ((state === 'green' || state === 'idle') && queued.length) {
    const free = project.machines
      .map((id) => machines.find((m) => m.id === id))
      .filter((m): m is Machine => !!m && m.online && (!m.caps || m.caps.running < m.caps.maxRuns))
      .map((m) => m.name)
    if (free.length)
      capacity = {
        machines: free,
        text: `${free.join(' and ')} ${free.length === 1 ? 'is' : 'are'} idle with ${plural(queued.length, 'task')} ready to start.`,
        picks: queued.slice(0, 3),
      }
  }

  return {
    projectId: pid,
    name: (project as { name: string }).name,
    state,
    lanes,
    asks,
    hotAsks,
    builds: releaseStrip({
      project: input.project,
      landings: input.landings,
      builds: input.builds,
      machines: input.machines,
      inbox: input.inbox,
      autoLand,
      now: input.now,
    }),
    counts: { needs, running, landedToday, queued: queued.length },
    degraded: { hub: frozen, machines: offlineMachines },
    locked: !!input.locked,
    autoLand,
    cards,
    ...(capacity ? { capacity } : {}),
  }
}

export { LANE_COLUMNS, localDayStart }
